import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, X, MagnifyingGlass, FilePdf, Play, ClockClockwise, CaretDown, CaretUp, ListBullets, CalendarBlank, ShareNetwork, BookmarkSimple, Bookmark } from "@phosphor-icons/react";
import { exportRoutinePDF } from "../../lib/pdf";
import LiveSession from "../../components/LiveSession";
import ErrorBoundary from "../../components/ErrorBoundary";
import { RPEEvolutionChart, WeeklyLoadChart } from "../../components/WorkoutCharts";
import FatigueIndex from "../../components/FatigueIndex";
import WeeklyCalendar from "../../components/WeeklyCalendar";
import ExerciseMedia from "../../components/ExerciseMedia";
import ShareModal from "../../components/routines/ShareModal";
import SessionHistory from "../../components/routines/SessionHistory";

function ExercisePicker({ onPick, onClose }) {
  const [exercises, setExercises] = useState([]);
  const [filter, setFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  useEffect(() => {
    api.get("/exercises").then((r) => setExercises(r.data)).catch(() => {});
  }, []);
  // Solo ejercicios "parent" en grid principal
  const parents = exercises.filter((e) => !e.parent_id);
  const filtered = parents.filter((e) =>
    e.name.toLowerCase().includes(filter.toLowerCase()) ||
    (e.category || "").toLowerCase().includes(filter.toLowerCase())
  );
  const variantsOf = (parentId) => exercises.filter((e) => e.parent_id === parentId);

  const handleCardClick = (ex) => {
    const hasVariants = variantsOf(ex.exercise_id).length > 0;
    if (hasVariants) {
      setExpandedId(expandedId === ex.exercise_id ? null : ex.exercise_id);
    } else {
      onPick(ex);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="exercise-picker-modal">
      <div className="card-base w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-app flex items-center justify-between">
          <h3 className="font-display font-black uppercase text-2xl">Elegir Ejercicio</h3>
          <button onClick={onClose} className="text-muted hover:text-white"><X size={22} weight="bold" /></button>
        </div>
        <div className="p-5 border-b border-app relative">
          <MagnifyingGlass size={18} className="absolute left-8 top-1/2 -translate-y-1/2 text-muted" />
          <input className="input-base pl-10" placeholder="Buscar ejercicio..." value={filter} onChange={(e) => setFilter(e.target.value)} autoFocus data-testid="picker-search" />
        </div>
        <div className="overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((ex) => {
              const vs = variantsOf(ex.exercise_id);
              return (
                <button key={ex.exercise_id} onClick={() => handleCardClick(ex)} className={`card-base text-left transition-colors overflow-hidden ${expandedId === ex.exercise_id ? "!border-[#FF3B30]" : "hover:!border-[#FF3B30]"}`} data-testid={`pick-exercise-${ex.exercise_id}`}>
                  <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
                    <ExerciseMedia exercise={ex} size="sm" autoHover={false} />
                    {vs.length > 0 && (
                      <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold bg-[#FF3B30] text-white px-1.5 py-0.5">
                        +{vs.length}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-primary-app mb-1">{ex.category || "General"}</div>
                    <div className="font-display font-bold uppercase text-sm line-clamp-2">{ex.name}</div>
                    {vs.length > 0 && (
                      <div className="text-[10px] text-muted mt-1">{expandedId === ex.exercise_id ? "Click para plegar ▲" : `Ver ${vs.length} variantes ▼`}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {/* Variantes del ejercicio expandido */}
          {expandedId && (() => {
            const parent = exercises.find((e) => e.exercise_id === expandedId);
            const vs = variantsOf(expandedId);
            if (!parent || vs.length === 0) return null;
            return (
              <div className="bg-[#0A0A0A] border border-app p-4" data-testid="picker-variants">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-muted">Variantes de <span className="text-white font-bold">{parent.name}</span></div>
                  <button onClick={() => onPick(parent)} className="text-[10px] uppercase tracking-widest font-bold text-muted hover:text-[#FF3B30]" data-testid="pick-parent-direct">
                    Usar el madre →
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {vs.map((v) => (
                    <button key={v.exercise_id} onClick={() => onPick(v)} className="card-base !border-app hover:!border-[#FF3B30] text-left transition-colors overflow-hidden" data-testid={`pick-variant-${v.exercise_id}`}>
                      <div className="aspect-square bg-[#141414] overflow-hidden relative">
                        <ExerciseMedia exercise={v} size="sm" autoHover={false} />
                        {v.difficulty && (
                          <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white" style={{
                            backgroundColor: v.difficulty === "avanzado" ? "#FF3B30" : v.difficulty === "intermedio" ? "#FFD60A" : "#32D74B"
                          }}>
                            {v.difficulty === "principiante" ? "Princ." : v.difficulty === "intermedio" ? "Interm." : "Avanz."}
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="font-display font-bold uppercase text-sm line-clamp-2">{v.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function RoutineEditor({ userId, routine, initialDate, seedTemplate, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: routine?.name || seedTemplate?.name || "",
    date: routine?.date || initialDate || new Date().toISOString().slice(0, 10),
    objectives: routine?.objectives || seedTemplate?.objectives || "",
    exercises: routine?.exercises || seedTemplate?.exercises || [],
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  // Estado de recurrencia (solo para creación). No aplica al editar.
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [recurrence, setRecurrence] = useState({
    days: [],        // 0=Lun .. 6=Dom
    weeks: 4,
    startDate: "",   // vacío = usar form.date como default
  });
  const isEditing = !!routine?.routine_id;

  const addExercise = (ex) => {
    setForm((f) => ({
      ...f,
      exercises: [...f.exercises, {
        exercise_id: ex.exercise_id,
        exercise_name: ex.name,
        image_url: ex.image_url,
        video_url: ex.video_url,
        media_type: ex.media_type,
        weight_kg: 0, reps: 0, sets: 0, distance_m: 0, duration_min: 0, micro_pause_sec: 0, macro_pause_sec: 0,
        order: f.exercises.length, notes: "",
      }],
    }));
    setPickerOpen(false);
  };

  const updateEx = (i, field, value) => {
    const arr = [...form.exercises];
    arr[i] = { ...arr[i], [field]: value };
    setForm({ ...form, exercises: arr });
  };

  const removeEx = (i) => {
    const arr = form.exercises.filter((_, idx) => idx !== i);
    setForm({ ...form, exercises: arr });
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Nombre de la plantilla:", form.name || "");
    if (!name || !name.trim()) return;
    try {
      await api.post("/routine-templates", {
        name: name.trim(),
        description: null,
        objectives: form.objectives || null,
        exercises: form.exercises.map((e, i) => ({
          ...e,
          order: i,
          weight_kg: Number(e.weight_kg) || 0,
          reps: Number(e.reps) || 0,
          sets: Number(e.sets) || 0,
          distance_m: Number(e.distance_m) || 0,
          duration_min: Number(e.duration_min) || 0,
          micro_pause_sec: Number(e.micro_pause_sec) || 0,
          macro_pause_sec: Number(e.macro_pause_sec) || 0,
        })),
      });
      toast.success("Plantilla guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: form.name,
        date: form.date,
        objectives: form.objectives,
        exercises: form.exercises.map((e, i) => ({
          ...e,
          order: i,
          weight_kg: Number(e.weight_kg) || 0,
          reps: Number(e.reps) || 0,
          sets: Number(e.sets) || 0,
          distance_m: Number(e.distance_m) || 0,
          duration_min: Number(e.duration_min) || 0,
          micro_pause_sec: Number(e.micro_pause_sec) || 0,
          macro_pause_sec: Number(e.macro_pause_sec) || 0,
        })),
      };
      // Recurrencia solo en creación. Si hay días seleccionados la enviamos.
      if (!isEditing && recurrenceOpen && recurrence.days.length > 0 && Number(recurrence.weeks) > 0) {
        payload.recurrence_days = recurrence.days;
        payload.recurrence_weeks = Number(recurrence.weeks);
        payload.recurrence_start_date = recurrence.startDate || form.date;
      }
      if (routine?.routine_id) {
        await api.put(`/routines/${routine.routine_id}`, payload);
        toast.success("Rutina guardada");
      } else {
        const { data } = await api.post(`/users/${userId}/routines`, payload);
        const count = data?.series_count || 1;
        toast.success(count > 1 ? `Rutina guardada · ${count} sesiones creadas` : "Rutina guardada");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="card-base p-6" data-testid="routine-editor">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-black uppercase text-2xl">{routine ? "Editar Rutina" : "Nueva Rutina"}</h3>
        <button onClick={onClose} className="text-muted hover:text-white"><X size={22} weight="bold" /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Nombre</label>
          <input className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="routine-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted block mb-2">Fecha</label>
          <input type="date" className="input-base" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="routine-date" />
        </div>
      </div>
      <div className="mb-5">
        <label className="text-xs uppercase tracking-widest text-muted block mb-2">Objetivos del día</label>
        <textarea className="input-base" rows="2" value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} placeholder="Ej: Fuerza máxima tren inferior" data-testid="routine-objectives" />
      </div>

      {/* Recurrencia: solo visible al crear una nueva rutina */}
      {!isEditing && (
        <div className="mb-5 border border-app" data-testid="recurrence-section">
          <button
            type="button"
            onClick={() => setRecurrenceOpen((v) => !v)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-[#0A0A0A] transition-colors"
            data-testid="recurrence-toggle"
          >
            <div className="flex items-center gap-2">
              <ClockClockwise size={16} weight="bold" className="text-primary-app" />
              <span className="text-xs uppercase tracking-widest font-bold">Repetir rutina</span>
              {recurrenceOpen && recurrence.days.length > 0 && (
                <span className="text-[10px] uppercase tracking-widest text-muted">
                  · {recurrence.days.length} días × {recurrence.weeks || 0} {Number(recurrence.weeks) === 1 ? "semana" : "semanas"}
                  = {recurrence.days.length * (Number(recurrence.weeks) || 0)} sesiones
                </span>
              )}
            </div>
            {recurrenceOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          </button>
          {recurrenceOpen && (
            <div className="p-4 border-t border-app space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Días de la semana</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { d: 0, l: "Lun" }, { d: 1, l: "Mar" }, { d: 2, l: "Mié" },
                    { d: 3, l: "Jue" }, { d: 4, l: "Vie" }, { d: 5, l: "Sáb" }, { d: 6, l: "Dom" },
                  ].map((x) => {
                    const active = recurrence.days.includes(x.d);
                    return (
                      <button
                        key={x.d}
                        type="button"
                        onClick={() => setRecurrence((r) => ({
                          ...r,
                          days: active ? r.days.filter((d) => d !== x.d) : [...r.days, x.d].sort((a, b) => a - b),
                        }))}
                        className={`min-w-[52px] text-[10px] uppercase tracking-widest font-bold border px-3 py-2 transition-colors ${
                          active ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                        }`}
                        data-testid={`rec-day-${x.d}`}
                      >
                        {x.l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted block mb-2">Fecha de inicio</label>
                  <input
                    type="date"
                    className="input-base !w-44"
                    value={recurrence.startDate || form.date}
                    onChange={(e) => setRecurrence((r) => ({ ...r, startDate: e.target.value }))}
                    data-testid="rec-start-date"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted block mb-2">Cantidad de semanas</label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    className="input-base !w-32"
                    value={recurrence.weeks}
                    onChange={(e) => {
                      // Permitir vacío mientras el usuario edita; no forzamos mínimo
                      // hasta que salga del campo (onBlur). Así puede borrar todo
                      // y reescribir sin quedar atrapado en "1".
                      const v = e.target.value;
                      setRecurrence((r) => ({ ...r, weeks: v === "" ? "" : Math.min(52, Math.max(0, Number(v) || 0)) }));
                    }}
                    onBlur={(e) => {
                      // Al salir del campo, asegurar mínimo 1
                      const n = Number(e.target.value) || 1;
                      setRecurrence((r) => ({ ...r, weeks: Math.max(1, Math.min(52, n)) }));
                    }}
                    data-testid="rec-weeks"
                  />
                </div>
                <div className="text-[11px] text-muted max-w-md">
                  Se alineará al lunes de la semana del{" "}
                  <strong className="text-white">{recurrence.startDate || form.date}</strong>.
                  {" "}Se crearán {recurrence.days.length * (Number(recurrence.weeks) || 0) || 0} sesiones con los mismos ejercicios,
                  editable cada una de forma individual.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-bold uppercase text-lg">Ejercicios</h4>
        <button onClick={() => setPickerOpen(true)} className="btn-secondary flex items-center gap-2" data-testid="add-exercise-btn">
          <Plus size={14} weight="bold" /> Agregar ejercicio
        </button>
      </div>

      {form.exercises.length === 0 ? (
        <div className="border border-dashed border-[#262626] p-8 text-center text-muted">Aún no hay ejercicios. Agregá desde la galería.</div>
      ) : (
        <div className="space-y-3">
          {form.exercises.map((ex, i) => (
            <div key={i} className="card-base !border-app p-4" data-testid={`routine-ex-${i}`}>
              <div className="flex gap-4">
                {(ex.image_url || ex.video_url) && (
                  <div className="w-24 h-24 flex-shrink-0 hidden sm:block overflow-hidden bg-[#0A0A0A]">
                    <ExerciseMedia exercise={ex} autoHover={false} size="sm" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div className="font-display font-bold uppercase text-lg">{ex.exercise_name}</div>
                    <button onClick={() => removeEx(i)} className="text-muted hover:text-[#FF3B30]" data-testid={`remove-ex-${i}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                    {[
                      { key: "weight_kg", label: "Peso (kg)" },
                      { key: "reps", label: "Reps" },
                      { key: "sets", label: "Series" },
                      { key: "distance_m", label: "Distancia (m)" },
                      { key: "duration_min", label: "Tiempo (min)" },
                      { key: "micro_pause_sec", label: "Micro Pausa (s)" },
                      { key: "macro_pause_sec", label: "Macro Pausa (s)" },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="text-[10px] uppercase tracking-widest text-muted block mb-1">{f.label}</label>
                        <input type="number" step="any" className="input-base !py-2" value={ex[f.key]} onChange={(e) => updateEx(i, f.key, e.target.value)} data-testid={`ex-${i}-${f.key}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3 mt-6 flex-wrap">
        <button onClick={handleSave} className="btn-primary flex items-center gap-2" data-testid="save-routine-btn">
          <FloppyDisk size={16} weight="bold" /> Guardar Rutina
        </button>
        <button onClick={onClose} className="btn-secondary">Cancelar</button>
        <button
          onClick={saveAsTemplate}
          disabled={form.exercises.length === 0 || !form.name.trim()}
          className="btn-secondary flex items-center gap-2 ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
          title="Guardar esta rutina como plantilla para reutilizarla en otros atletas"
          data-testid="save-template-btn"
        >
          <BookmarkSimple size={16} weight="bold" /> Guardar como plantilla
        </button>
      </div>

      {pickerOpen && <ExercisePicker onPick={addExercise} onClose={() => setPickerOpen(false)} />}
    </div>
  );
}

function TemplatesPicker({ onUse, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/routine-templates");
      setTemplates(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla? Las rutinas ya asignadas no se tocan.")) return;
    try {
      await api.delete(`/routine-templates/${id}`);
      toast.success("Plantilla eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/routine-templates/${editing.template_id}`, {
        name: editing.name,
        description: editing.description,
        objectives: editing.objectives,
        exercises: editing.exercises || [],
      });
      toast.success("Plantilla actualizada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = templates.filter((t) =>
    t.name.toLowerCase().includes(filter.toLowerCase()) ||
    (t.objectives || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="templates-modal">
      <div className="card-base w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-app flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-black uppercase text-2xl tracking-tighter">Plantillas de Rutina</h3>
            <div className="text-xs text-muted mt-1">Reutilizá rutinas pre-armadas en múltiples atletas</div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white" data-testid="close-templates"><X size={22} weight="bold" /></button>
        </div>

        <div className="p-5 border-b border-app relative">
          <MagnifyingGlass size={18} className="absolute left-8 top-1/2 -translate-y-1/2 text-muted" />
          <input
            className="input-base pl-10"
            placeholder="Buscar plantilla..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
            data-testid="templates-search"
          />
        </div>

        <div className="overflow-y-auto p-5 flex-1">
          {loading ? (
            <div className="text-center text-muted text-sm py-8">Cargando plantillas...</div>
          ) : templates.length === 0 ? (
            <div className="text-center text-muted text-sm py-8">
              Todavía no hay plantillas.<br />
              Creá una rutina con ejercicios y tocá "Guardar como plantilla" para empezar.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="templates-grid">
              {filtered.map((t) => (
                <div key={t.template_id} className="card-base !border-app p-4 hover:!border-[#FF3B30] transition-colors" data-testid={`template-card-${t.template_id}`}>
                  {editing?.template_id === t.template_id ? (
                    <div className="space-y-2">
                      <input className="input-base" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nombre" data-testid="template-edit-name" />
                      <textarea className="input-base" rows="2" value={editing.objectives || ""} onChange={(e) => setEditing({ ...editing, objectives: e.target.value })} placeholder="Objetivos" />
                      <div className="flex gap-2">
                        <button onClick={handleSaveEdit} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-1" data-testid="template-save-edit"><FloppyDisk size={12} weight="bold" /> Guardar</button>
                        <button onClick={() => setEditing(null)} className="btn-secondary !py-2 !px-3 text-xs">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="font-display font-black uppercase tracking-tight text-lg line-clamp-1">{t.name}</div>
                          {t.objectives && <div className="text-xs text-muted line-clamp-2 mt-0.5">{t.objectives}</div>}
                        </div>
                        {t.use_count > 0 && (
                          <span className="text-[9px] uppercase tracking-widest font-bold bg-[#262626] text-muted px-1.5 py-0.5 whitespace-nowrap">
                            Usada {t.use_count}×
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted mb-3">
                        {t.exercises?.length || 0} ejercicios
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => onUse(t)} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-1" data-testid={`use-template-${t.template_id}`}>
                          <Plus size={12} weight="bold" /> Aplicar al atleta
                        </button>
                        <button onClick={() => setEditing({ ...t })} className="btn-secondary !py-2 !px-3 text-xs" data-testid={`edit-template-${t.template_id}`}>
                          <PencilSimple size={12} weight="bold" />
                        </button>
                        <button onClick={() => handleDelete(t.template_id)} className="btn-secondary !py-2 !px-3 text-xs hover:!border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`delete-template-${t.template_id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {filtered.length === 0 && templates.length > 0 && (
                <div className="col-span-full text-center text-muted text-sm py-6">Sin resultados</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function RoutinesTab({ userId, isAdmin, targetUser }) {
  const [routines, setRoutines] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [liveRoutine, setLiveRoutine] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [wellness, setWellness] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("athlon_routines_view") || "list");
  const [shareRoutine, setShareRoutine] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateSeed, setTemplateSeed] = useState(null); // Template a aplicar al abrir el editor

  const changeView = (v) => {
    setView(v);
    localStorage.setItem("athlon_routines_view", v);
  };

  // Orden: primero las rutinas de hoy y futuras (más cercana primero),
  // después las pasadas (más recientes primero). Así el atleta ve al tope
  // qué le toca próximamente y las pasadas quedan como historial abajo.
  const sortedRoutines = React.useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const future = [];
    const past = [];
    for (const r of routines) {
      const d = r.date || "";
      if (d >= today) future.push(r);
      else past.push(r);
    }
    future.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    past.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return [...future, ...past];
  }, [routines]);

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/routines`);
      setRoutines(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/sessions`);
      setHistory(data);
    } catch {
      /* silent */
    }
  };
  const loadWellness = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/wellness`);
      setWellness(data);
    } catch {
      /* silent */
    }
  };
  useEffect(() => { load(); loadHistory(); loadWellness(); /* eslint-disable-next-line */ }, [userId]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar rutina?")) return;
    try {
      await api.delete(`/routines/${id}`);
      toast.success("Eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteSeries = async (seriesId) => {
    if (!seriesId) return;
    if (!window.confirm("¿Eliminar TODAS las sesiones de esta serie (todas las repeticiones)?")) return;
    try {
      const { data } = await api.delete(`/routine-series/${seriesId}`);
      toast.success(`Serie eliminada (${data?.deleted || 0} sesiones)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (creating || editing) {
    return (
      <RoutineEditor
        userId={userId}
        routine={editing}
        initialDate={createDate}
        seedTemplate={templateSeed}
        onClose={() => { setCreating(false); setEditing(null); setCreateDate(null); setTemplateSeed(null); }}
        onSaved={() => { setCreating(false); setEditing(null); setCreateDate(null); setTemplateSeed(null); load(); }}
      />
    );
  }

  return (
    <div>
      {liveRoutine && (
        <ErrorBoundary context="LiveSession" onReset={() => { setLiveRoutine(null); loadHistory(); }}>
          <LiveSession routine={liveRoutine} userId={userId} onClose={() => { setLiveRoutine(null); loadHistory(); }} />
        </ErrorBoundary>
      )}

      {templatesOpen && (
        <TemplatesPicker
          onUse={(t) => {
            // Abrimos el editor pre-llenado con los ejercicios de la plantilla.
            // El admin puede ajustar fecha y recurrencia antes de guardar.
            setTemplateSeed({
              name: t.name,
              objectives: t.objectives || "",
              exercises: (t.exercises || []).map((e, i) => ({ ...e, order: i })),
            });
            setTemplatesOpen(false);
            setCreateDate(null);
            setCreating(true);
          }}
          onClose={() => setTemplatesOpen(false)}
        />
      )}

      {/* Header: view toggle + new button */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="inline-flex border border-app" data-testid="routines-view-toggle">
          <button
            onClick={() => changeView("list")}
            className={`px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition-colors ${
              view === "list" ? "bg-[#FF3B30] text-white" : "bg-transparent text-muted hover:text-white"
            }`}
            data-testid="view-list-btn"
          >
            <ListBullets size={14} weight="bold" /> Lista
          </button>
          <button
            onClick={() => changeView("calendar")}
            className={`px-4 py-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition-colors ${
              view === "calendar" ? "bg-[#FF3B30] text-white" : "bg-transparent text-muted hover:text-white"
            }`}
            data-testid="view-calendar-btn"
          >
            <CalendarBlank size={14} weight="bold" /> Calendario
          </button>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setTemplatesOpen(true)} className="btn-secondary flex items-center gap-2" data-testid="open-templates-btn">
              <Bookmark size={16} weight="bold" /> Plantillas
            </button>
            <button onClick={() => { setCreateDate(null); setCreating(true); }} className="btn-primary flex items-center gap-2" data-testid="new-routine-btn">
              <Plus size={16} weight="bold" /> Nueva Rutina
            </button>
          </div>
        )}
      </div>

      {view === "calendar" ? (
        <WeeklyCalendar
          routines={routines}
          sessions={history}
          isAdmin={isAdmin}
          onStartLive={(r) => setLiveRoutine(r)}
          onEdit={(r) => setEditing(r)}
          onDelete={handleDelete}
          onExportPDF={(r) => exportRoutinePDF(r, targetUser)}
          onShare={(r) => setShareRoutine(r)}
          onCreateForDate={(iso) => { setCreateDate(iso); setCreating(true); }}
        />
      ) : routines.length === 0 ? (
        <div className="card-base p-12 text-center text-muted">No hay rutinas todavía</div>
      ) : (
        <div className="space-y-4" data-testid="routines-list">
          {sortedRoutines.map((r) => (
            <div key={r.routine_id} className="card-base p-6 hover:border-[#FF3B30] transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="text-xs uppercase tracking-widest text-primary-app">{r.date}</div>
                    {r.series_id && (
                      <span className="text-[9px] uppercase tracking-widest font-bold bg-[#FF3B30] text-white px-1.5 py-0.5" data-testid={`series-badge-${r.routine_id}`}>
                        En serie
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-black uppercase text-2xl">{r.name}</h3>
                  {r.objectives && <p className="text-muted text-sm mt-2">Obj: {r.objectives}</p>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {r.exercises && r.exercises.length > 0 && (
                    <button
                      onClick={() => setLiveRoutine(r)}
                      className="btn-primary !py-2 !px-4 flex items-center gap-2"
                      data-testid={`live-routine-${r.routine_id}`}
                    >
                      <Play size={14} weight="fill" /> Iniciar Sesión
                    </button>
                  )}
                  <button onClick={() => exportRoutinePDF(r, targetUser)} className="btn-secondary !py-2 !px-3 flex items-center gap-1" title="Exportar PDF" data-testid={`pdf-routine-${r.routine_id}`}>
                    <FilePdf size={14} weight="bold" />
                  </button>
                  <button
                    onClick={() => setShareRoutine(r)}
                    className={`btn-secondary !py-2 !px-3 flex items-center gap-1 ${r.is_shared ? "!border-[#32D74B] text-[#32D74B]" : ""}`}
                    title={r.is_shared ? "Link público activo" : "Compartir rutina"}
                    data-testid={`share-routine-${r.routine_id}`}
                  >
                    <ShareNetwork size={14} weight="bold" />
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => setEditing(r)} className="btn-secondary !py-2 !px-3" data-testid={`edit-routine-${r.routine_id}`}>
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(r.routine_id)} className="btn-secondary !py-2 !px-3 hover:!border-[#FF3B30]" data-testid={`delete-routine-${r.routine_id}`} title="Eliminar esta sesión">
                        <Trash size={14} weight="bold" />
                      </button>
                      {r.series_id && (
                        <button
                          onClick={() => handleDeleteSeries(r.series_id)}
                          className="btn-secondary !py-2 !px-3 hover:!border-[#FF3B30] text-[#FF3B30]"
                          title="Eliminar serie completa (todas las repeticiones)"
                          data-testid={`delete-series-${r.routine_id}`}
                        >
                          <Trash size={14} weight="bold" /> <span className="text-[10px] font-bold">SERIE</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {r.exercises?.map((ex, i) => (
                  <div key={i} className="flex gap-3 bg-[#0A0A0A] border border-app p-3">
                    {(ex.image_url || ex.video_url) && (
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden">
                        <ExerciseMedia exercise={ex} autoHover={false} size="sm" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold uppercase text-sm truncate">{ex.exercise_name}</div>
                      <div className="text-xs text-muted mt-1">
                        {ex.sets}×{ex.reps} · {ex.weight_kg}kg{ex.distance_m ? ` · ${ex.distance_m}m` : ""}{ex.duration_min ? ` · ${ex.duration_min}min` : ""} · μ{ex.micro_pause_sec}s / M{ex.macro_pause_sec}s
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <SessionHistory history={history} setShowHistory={setShowHistory} showHistory={showHistory} isAdmin={isAdmin} onDelete={async (id) => {
        if (!window.confirm("¿Eliminar esta sesión del historial?")) return;
        try {
          await api.delete(`/sessions/${id}`);
          toast.success("Sesión eliminada");
          loadHistory();
        } catch (err) {
          toast.error(formatApiError(err));
        }
      }} />

      {history.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-6" data-testid="workout-charts">
          <FatigueIndex sessions={history} wellnessEntries={wellness} />
          <WeeklyLoadChart sessions={history} />
          <RPEEvolutionChart sessions={history} />
        </div>
      )}

      {shareRoutine && (
        <ShareModal
          routine={shareRoutine}
          onClose={() => setShareRoutine(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}

